import { FilterLegacy } from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { ConceptDetail } from '../hooks/common/useGetAllConceptGroupFromDefinition';

// Maps a concept group ID to its corresponding name.
// If the ID is not found in the concept list, it simply returns the ID.
export const mapConceptGroupIdToName = (id: string, conceptList = [] as ConceptGroup[]) =>
  conceptList.find((concept) => concept.conceptGroupId === id)?.conceptGroupName || id;

// Checks if a concept with the given ID exists in the concept list.
export const checkIfConceptExists = (id: string, conceptList = [] as ConceptGroup[]) =>
  conceptList.some((concept) => concept.conceptGroupId === id);

// Sanitizes the given ID by removing any leading or trailing double quotes.
export const getSanitizeId = (id: string): string => id.replace(/^"|"$/g, '');

// Extracts the concept group value from the concept group string.
export const getConceptGroupValue = (conceptGroup: string): string => conceptGroup.split('::')[1];

// Extracts all concepts from the concept string and constructs a ConceptDetail object for each.
// It also checks if the concept exists in the concept list and sets the isExist flag accordingly.
// TODO: Simplify the function and it's arguments. Use an object to pass the arguments. -- @Jawad
export const getAllConceptFromConceptGroupString = (
  currentConceptDetails: ConceptDetail[],
  conceptAttribute: string,
  str?: string,
  conceptList?: ConceptGroup[]
): ConceptDetail[] => {
  const conceptGroups = str?.split(',') || [];
  const concepts: ConceptDetail[] = conceptGroups
    .map((conceptGroup) => {
      const conceptGroupValue = getConceptGroupValue(conceptGroup);
      const sanitizedConceptGroupValue = getSanitizeId(conceptGroupValue);
      return {
        id: sanitizedConceptGroupValue,
        attribute: conceptAttribute,
        name: mapConceptGroupIdToName(sanitizedConceptGroupValue, conceptList),
        isExist: checkIfConceptExists(sanitizedConceptGroupValue, conceptList),
      };
    })
    .filter(
      (concept) =>
        !currentConceptDetails.find(
          (c) => c.attribute === concept.attribute && c.name === concept.name
        )
    );
  return concepts;
};

// Retrieves a list of concept details based on different filter types.
// TODO: Simplify the function and it's arguments. Use an object to pass the arguments. -- @Jawad
export const getConceptFromDifferentFilterTypes = (
  currentConceptDetails: ConceptDetail[],
  conceptFiltersList: FilterLegacy[],
  conceptAttributeLocationIndex: number,
  conceptGroupLocationIndex: number,
  conceptList?: ConceptGroup[]
): ConceptDetail[] => {
  // Reduce the list of filters to a list of concept details
  return conceptFiltersList.reduce<ConceptDetail[]>((acc, filter) => {
    // Get the concept attribute and group from the filter
    const conceptAttribute = `${filter.inputFields[conceptAttributeLocationIndex].value?.replace(
      '_',
      ' '
    )}`;
    const conceptGroup = filter.inputFields[conceptGroupLocationIndex].value;
    // Get the list of concepts from the concept group string
    const concepts = getAllConceptFromConceptGroupString(
      currentConceptDetails,
      conceptAttribute,
      conceptGroup,
      conceptList
    );
    // Add the concepts to the accumulator
    return acc.concat(concepts);
  }, []);
};

// Checks if a filter has a concept that doesn't exist in the concept list
export const checkFilterForMissingConcept = ({
  filter,
  conceptFieldIndex,
  allConceptsList,
}: {
  filter: FilterLegacy | undefined;
  conceptFieldIndex: number;
  allConceptsList: ConceptGroup[] | undefined;
}) => {
  if (!filter?.inputFields?.[conceptFieldIndex]?.value) {
    return true;
  }

  const conceptGroupsToCheck = filter.inputFields[conceptFieldIndex].value.split(',');
  const doesConceptExist = conceptGroupsToCheck.some((conceptGroup) => {
    const conceptValue = getConceptGroupValue(conceptGroup);
    const sanitizedConceptValue = conceptValue?.replace(/['"`]/g, '');
    const conceptId = getSanitizeId(sanitizedConceptValue);
    return checkIfConceptExists(conceptId, allConceptsList);
  });

  if (!doesConceptExist) {
    return true;
  }

  return false;
};
